import React, { useState } from 'react';
import { useSavedListings, useToggleSaveListing } from '@/hooks/useSavedListings';
import { CandidateTable } from '@/components/jobs/CandidateTable';
import { CandidateDetailModal } from '@/components/jobs/CandidateDetailModal';
import { EmptyState, Spinner } from '@/components/common';
import { Menu, MenuItem, ListItemIcon } from '@mui/material';
import { Heart } from 'lucide-react';
import dayjs from '@/lib/dayjs';
import { useNavigate } from 'react-router-dom';
import { formatSalaryRange } from '@/lib/utils';

export default function SavedCandidatesPage() {
  const navigate = useNavigate();
  const { data: savedListings, isLoading } = useSavedListings();
  const { toggleSave } = useToggleSaveListing();

  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionCandidateId, setActionCandidateId] = useState<string | null>(null);

  // Filter only candidates
  const savedCandidatesList = savedListings?.data?.filter((item: any) => item.entityType === 'JobOfferRequest') || [];

  // Map to the format CandidateTable expects
  const mappedCandidates = savedCandidatesList.map(item => {
    // Note: The backend might return candidateProfile or we might need to adjust this depending on the exact API response.
    // Assuming backend returns candidateProfile inside the saved listing item
    const c = (item as any).candidateProfile || (item as any).jobOffer || (item as any).entity;
    if (!c) {
      return {
        id: item.entityId,
        fullName: 'Hồ sơ ứng viên (Đã lưu)',
        email: '---',
        position: '---',
        categoryAndType: '---',
        experience: '---',
        salary: '---',
        location: '---',
        status: 'screening',
        statusLabel: 'Chưa cập nhật',
        isReal: true,
        rawProfile: null,
      };
    }

    return {
      id: c.id,
      isReal: true,
      fullName: c.fullName || 'Chưa cập nhật',
      email: c.email || 'Chưa cập nhật',
      phoneNumber: c.phoneNumber || '',
      position: c.desiredPosition || 'Chưa cập nhật vị trí',
      categoryAndType: `${c.jobCategoryName || ''} ${c.jobCategoryName && c.jobType ? '•' : ''} ${c.jobType || ''}`.trim() || 'Chưa cập nhật',
      experience: c.experience ? `${c.experience} năm` : 'Chưa cập nhật KN',
      education: c.education || 'Chưa cập nhật HV',
      salary: formatSalaryRange(c.expectedSalaryMin, c.expectedSalaryMax),
      location: c.province?.name || c.location || 'Chưa cập nhật',
      status: 'screening',
      statusLabel: 'Đã lưu',
      date: dayjs(item.createdAt).format('DD/MM/YYYY'),
      avatar: c.images?.find((img: any) => img.isPrimary)?.image?.publicUrl || null,
      rawProfile: c
    };
  });

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, candidateId: string) => {
    setAnchorEl(event.currentTarget);
    setActionCandidateId(candidateId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActionCandidateId(null);
  };

  const handleUnsave = () => {
    if (actionCandidateId) {
      toggleSave(actionCandidateId, 'JobOfferRequest');
    }
    handleCloseMenu();
  };

  return (
    <div className="w-full relative min-h-[400px]">
      {isLoading && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
          <Spinner />
        </div>
      )}

      {!isLoading && mappedCandidates.length === 0 ? (
        <EmptyState title="Chưa lưu ứng viên nào" description="Bạn có thể tìm kiếm và lưu ứng viên để xem lại sau." icon="🧑‍💼" />
      ) : (
        <>
          <CandidateTable
            candidates={mappedCandidates}
            onViewDetail={(c) => setSelectedCandidate(c)}
            onUnsave={(c) => toggleSave(c.id, 'JobOfferRequest')}
            pageSize={12}
          />

          <CandidateDetailModal
            open={!!selectedCandidate}
            onClose={() => setSelectedCandidate(null)}
            candidate={selectedCandidate}
            onAccept={(id) => {
              toggleSave(id, 'JobOfferRequest');
            }}
          />

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={{
              '& .MuiPaper-root': {
                overflow: 'visible',
                filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
                mt: 1,
                borderRadius: '16px',
                border: '1px solid #f1f3f5',
                minWidth: '160px'
              }
            }}
          >
            <MenuItem onClick={handleUnsave} sx={{ fontSize: '13px', fontWeight: 'bold', color: '#ef4444', py: 1.5, '&:hover': { color: '#ef4444', backgroundColor: '#fef2f2' } }}>
              <ListItemIcon>
                <Heart size={16} className="text-[#ef4444]" />
              </ListItemIcon>
              Bỏ lưu ứng viên
            </MenuItem>
          </Menu>
        </>
      )}
    </div>
  );
}
